import { useEffect, useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { Link } from "react-router-dom";

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,300&display=swap');
  *, *::before, *::after { box-sizing: border-box; }
  html { scroll-behavior: smooth; }
`;

/* ───────────────────────── Shell ───────────────────────── */

const Page = styled.div`
  background: #fff;
  min-height: 100vh;
  font-family: "Mendl", sans-serif;
  color: #0a0010;
`;

const Container = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 clamp(1.25rem, 5vw, 3.5rem);
`;

/* ───────────────────────── Hero ───────────────────────── */

const Hero = styled.section`
  padding: clamp(6rem, 12vw, 8.5rem) 0 clamp(2.5rem, 5vw, 3.5rem);
  border-bottom: 1px solid rgba(168, 85, 247, 0.12);
`;

const Eyebrow = styled.div`
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: #a855f7;
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.6rem;

  &::before {
    content: "";
    width: 32px;
    height: 1px;
    background: #a855f7;
    display: inline-block;
  }
`;

const HeroTitle = styled.h1`
  font-size: clamp(2.4rem, 5.5vw, 4.2rem);
  font-weight: 600;
  line-height: 1.05;
  letter-spacing: -0.01em;
  margin-bottom: 1.2rem;
`;

const HeroMeta = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.85rem;
  font-weight: 400;
  color: rgba(10, 0, 16, 0.5);
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 1.5rem;
`;

const HeroLede = styled.p`
  font-family: "DM Sans", sans-serif;
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.8;
  color: rgba(10, 0, 16, 0.65);
  max-width: 640px;
  margin-top: 1.6rem;
`;

const AcceptBanner = styled.div`
  margin-top: 1.8rem;
  padding: 1.1rem 1.4rem;
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    rgba(232, 121, 249, 0.08),
    rgba(124, 58, 237, 0.08)
  );
  border: 1px solid rgba(168, 85, 247, 0.18);
  font-family: "DM Sans", sans-serif;
  font-size: 0.82rem;
  font-weight: 500;
  line-height: 1.7;
  color: #4c1d75;
  max-width: 760px;
`;

/* ───────────────────────── Layout ───────────────────────── */

const Layout = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: clamp(2rem, 4vw, 5rem);
  padding: clamp(3rem, 6vw, 4.5rem) 0 6rem;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const TocWrap = styled.nav`
  position: sticky;
  top: clamp(1.5rem, 4vh, 2.5rem);
  max-height: calc(100vh - 3rem);
  overflow-y: auto;
  padding-right: 0.5rem;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(168, 85, 247, 0.25);
    border-radius: 4px;
  }

  @media (max-width: 900px) {
    position: static;
    max-height: none;
    margin-bottom: 1rem;
  }
`;

const TocTitle = styled.div`
  font-size: 0.65rem;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(10, 0, 16, 0.35);
  margin-bottom: 1rem;
`;

const TocList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

const TocLink = styled.a`
  font-family: "DM Sans", sans-serif;
  font-size: 0.83rem;
  font-weight: 400;
  color: ${({ $active }) => ($active ? "#7c3aed" : "rgba(10, 0, 16, 0.55)")};
  text-decoration: none;
  display: block;
  line-height: 1.4;
  border-left: 2px solid
    ${({ $active }) => ($active ? "#a855f7" : "transparent")};
  padding-left: 0.75rem;
  transition:
    color 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    color: #7c3aed;
  }
`;

const Content = styled.div`
  max-width: 760px;
`;

const Section = styled.section`
  scroll-margin-top: 2rem;
  padding-bottom: clamp(2.2rem, 4vw, 3rem);
  margin-bottom: clamp(2.2rem, 4vw, 3rem);
  border-bottom: 1px solid rgba(168, 85, 247, 0.1);

  &:last-of-type {
    border-bottom: none;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(1.3rem, 2.2vw, 1.6rem);
  font-weight: 600;
  margin-bottom: 1.1rem;
  display: flex;
  align-items: baseline;
  gap: 0.6rem;

  span {
    font-size: 0.75rem;
    font-weight: 500;
    color: #a855f7;
    letter-spacing: 0.05em;
  }
`;

const SubTitle = styled.h3`
  font-size: 1.02rem;
  font-weight: 600;
  margin: 1.6rem 0 0.7rem;
  color: #1a0b2e;
`;

const P = styled.p`
  font-family: "DM Sans", sans-serif;
  font-size: 0.94rem;
  font-weight: 300;
  line-height: 1.85;
  color: rgba(10, 0, 16, 0.68);
  margin-bottom: 0.9rem;

  strong {
    font-weight: 600;
    color: #0a0010;
  }

  a {
    color: #7c3aed;
    text-decoration: none;
    font-weight: 500;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const List = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin: 0.6rem 0 1rem;
`;

const ListItem = styled.li`
  font-family: "DM Sans", sans-serif;
  font-size: 0.94rem;
  font-weight: 300;
  line-height: 1.75;
  color: rgba(10, 0, 16, 0.68);
  padding-left: 1.2rem;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0.55em;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #a855f7;
  }

  strong {
    font-weight: 600;
    color: #0a0010;
  }
`;

const Callout = styled.div`
  background: rgba(168, 85, 247, 0.05);
  border: 1px solid rgba(168, 85, 247, 0.15);
  border-radius: 10px;
  padding: 1rem 1.2rem;
  margin: 0.9rem 0;

  p {
    margin-bottom: 0;
  }
`;

/* Emergency / life-threatening situations get a distinct urgent treatment. */
const CriticalCallout = styled.div`
  background: rgba(220, 38, 38, 0.045);
  border: 1px solid rgba(220, 38, 38, 0.22);
  border-left: 3px solid #dc2626;
  border-radius: 8px;
  padding: 1rem 1.2rem;
  margin: 0.9rem 0;

  p {
    margin-bottom: 0;
    color: rgba(10, 0, 16, 0.75);
  }

  strong {
    color: #b91c1c;
  }

  a {
    color: #b91c1c;
    font-weight: 600;
  }
`;

const CriticalLabel = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #dc2626;
  margin-bottom: 0.5rem;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  margin: 1rem 0 1.4rem;
  border: 1px solid rgba(168, 85, 247, 0.15);
  border-radius: 10px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: "DM Sans", sans-serif;
  font-size: 0.85rem;
  min-width: 560px;

  th {
    text-align: left;
    font-weight: 600;
    font-size: 0.72rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #7c3aed;
    background: rgba(168, 85, 247, 0.07);
    padding: 0.8rem 1rem;
    border-bottom: 1px solid rgba(168, 85, 247, 0.15);
  }

  td {
    padding: 0.8rem 1rem;
    color: rgba(10, 0, 16, 0.68);
    font-weight: 300;
    line-height: 1.6;
    border-bottom: 1px solid rgba(168, 85, 247, 0.08);
    vertical-align: top;
  }

  tr:last-child td {
    border-bottom: none;
  }

  td:first-child {
    font-weight: 600;
    color: #0a0010;
  }
`;

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 0.8rem 0 1rem;
`;

const ContactRow = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.94rem;
  color: rgba(10, 0, 16, 0.68);

  b {
    color: #0a0010;
    font-weight: 600;
  }

  a {
    color: #7c3aed;
    text-decoration: none;
    font-weight: 500;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const BackHome = styled(Link)`
  font-family: "DM Sans", sans-serif;
  font-size: 0.85rem;
  color: #7c3aed;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 2rem;

  &:hover {
    text-decoration: underline;
  }
`;

/* ───────────────────────── Data ───────────────────────── */

const TOC = [
  { id: "scope", label: "1. Purpose & Scope" },
  { id: "contact", label: "2. How to Submit a Request" },
  { id: "emergency", label: "3. Emergency Requests" },
  { id: "legal-process", label: "4. Required Legal Process" },
  { id: "international", label: "5. Requests Outside the U.S." },
  { id: "data-available", label: "6. Data We May Hold" },
  { id: "data-not-available", label: "7. Data We Cannot Provide" },
  { id: "preservation", label: "8. Preservation Requests" },
  { id: "format", label: "9. What to Include" },
  { id: "response", label: "10. Our Response Process" },
  { id: "notice", label: "11. Notice to Users" },
  { id: "csam", label: "12. Child Safety Reporting" },
  { id: "costs", label: "13. Costs & Authentication" },
  { id: "no-waiver", label: "14. No Waiver" },
];

export default function LawEnforcementPage() {
  const [active, setActive] = useState(TOC[0].id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-15% 0px -70% 0px" },
    );
    TOC.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <Page>
      <GlobalStyle />

      <Hero>
        <Container>
          <Eyebrow>Legal</Eyebrow>
          <HeroTitle>Law Enforcement Guidelines</HeroTitle>
          <HeroMeta>
            <span>Effective Date: August 11, 2026</span>
          </HeroMeta>
          <HeroLede>
            These Guidelines explain how law enforcement agencies, government
            authorities, and other officials may request user information from
            NUDR INC ("NudR"), a company incorporated under the laws of the
            State of Delaware, U.S.A., with its registered office at 1105 N
            Buena Vista Street, Burbank, CA 91505. They are provided for
            informational purposes to help agencies direct requests correctly
            and are not a substitute for reviewing applicable law.
          </HeroLede>
          <AcceptBanner>
            NudR is committed to user safety and to cooperating with valid legal
            process. We are equally committed to protecting our users' privacy.
            We disclose user information only where we are legally required to
            do so, or where disclosure is necessary to prevent imminent harm —
            and we require appropriate legal process in every other case.
          </AcceptBanner>
        </Container>
      </Hero>

      <Container>
        <Layout>
          <TocWrap>
            <TocTitle>On this page</TocTitle>
            <TocList>
              {TOC.map((item) => (
                <li key={item.id}>
                  <TocLink href={`#${item.id}`} $active={active === item.id}>
                    {item.label}
                  </TocLink>
                </li>
              ))}
            </TocList>
          </TocWrap>

          <Content>
            {/* 1. Scope */}
            <Section id="scope">
              <SectionTitle>
                <span>01</span>Purpose and Scope
              </SectionTitle>
              <P>
                These Guidelines apply to requests for user information made to
                NudR by law enforcement agencies, prosecutors, courts,
                regulators, and other government authorities. They describe the
                legal process we require, the categories of information we may
                hold, and how to submit a request.
              </P>
              <P>
                These Guidelines do not apply to, and should not be used for:
              </P>
              <List>
                <ListItem>
                  Requests from private individuals, including civil litigants
                  and their counsel — those must follow applicable civil
                  discovery procedures.
                </ListItem>
                <ListItem>
                  User reports of harassment, abuse, or policy violations, which
                  are handled through the in-app reporting tools described in
                  our{" "}
                  <Link to="/guidelines" style={{ color: "#7c3aed" }}>
                    Community Guidelines
                  </Link>
                  .
                </ListItem>
                <ListItem>
                  General questions about how NudR handles personal information,
                  which are addressed in our{" "}
                  <Link to="/privacy" style={{ color: "#7c3aed" }}>
                    Privacy Policy
                  </Link>
                  .
                </ListItem>
              </List>
              <P>
                Nothing in these Guidelines constitutes consent to jurisdiction,
                a waiver of any objection, or an agreement to accept service of
                process by email or through any other informal method.
              </P>
            </Section>

            {/* 2. Contact */}
            <Section id="contact">
              <SectionTitle>
                <span>02</span>How to Submit a Request
              </SectionTitle>
              <P>
                All law enforcement requests should be submitted in writing on
                official letterhead, signed by the requesting officer, and sent
                to:
              </P>
              <ContactList>
                <ContactRow>
                  <b>Law Enforcement Requests:</b>{" "}
                  <a href="mailto:legal@nudr.app">legal@nudr.app</a>
                </ContactRow>
                <ContactRow>
                  <b>Trust &amp; Safety / Urgent Safety Matters:</b>{" "}
                  <a href="mailto:security@nudr.app">security@nudr.app</a>
                </ContactRow>
                <ContactRow>
                  <b>Postal Address:</b> NUDR INC, 1105 N Buena Vista Street,
                  Burbank, CA 91505, U.S.A.
                </ContactRow>
              </ContactList>
              <P>
                Please include <strong>"Law Enforcement Request"</strong> in the
                subject line, together with the requesting agency name and any
                internal reference number. Submitting a request by email is
                provided for convenience and speed; it does not constitute valid
                service of legal process where formal service is required by
                law.
              </P>
            </Section>

            {/* 3. Emergency */}
            <Section id="emergency">
              <SectionTitle>
                <span>03</span>Emergency Requests
              </SectionTitle>
              <CriticalCallout>
                <CriticalLabel>Imminent danger</CriticalLabel>
                <P>
                  If a request involves an{" "}
                  <strong>
                    imminent threat of death or serious physical injury
                  </strong>{" "}
                  to any person, mark the subject line{" "}
                  <strong>"EMERGENCY DISCLOSURE REQUEST"</strong> and send it to{" "}
                  <a href="mailto:security@nudr.app">security@nudr.app</a>. We
                  review emergency requests as a priority and may disclose
                  information without legal process where we form a good-faith
                  belief that disclosure is necessary to prevent that harm.
                </P>
              </CriticalCallout>
              <P>An emergency request must set out, at minimum:</P>
              <List>
                <ListItem>
                  The nature of the emergency and the specific harm you believe
                  is imminent.
                </ListItem>
                <ListItem>
                  Why the information requested is necessary to prevent that
                  harm.
                </ListItem>
                <ListItem>
                  The identifiers of the account(s) concerned (see Section 9).
                </ListItem>
                <ListItem>
                  The name, rank, agency, official email address, and direct
                  telephone number of the requesting officer.
                </ListItem>
              </List>
              <P>
                We assess each emergency request on its own facts and disclose
                only the information reasonably necessary to address the
                emergency. Submitting an emergency request does not guarantee
                disclosure.
              </P>
            </Section>

            {/* 4. Legal process */}
            <Section id="legal-process">
              <SectionTitle>
                <span>04</span>Required Legal Process
              </SectionTitle>
              <P>
                Outside of emergencies, NudR requires valid legal process before
                disclosing user information. As a U.S. company, we apply the
                standards of the Stored Communications Act, 18 U.S.C. §§ 2701 et
                seq., and other applicable law:
              </P>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Legal process</th>
                      <th>What it may compel</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Valid subpoena</td>
                      <td>
                        Basic subscriber information, such as the name provided
                        at registration, email address, account creation date,
                        and recent IP address history.
                      </td>
                    </tr>
                    <tr>
                      <td>Court order (18 U.S.C. § 2703(d))</td>
                      <td>
                        Basic subscriber information plus certain non-content
                        records, such as account activity logs and message
                        metadata.
                      </td>
                    </tr>
                    <tr>
                      <td>Search warrant</td>
                      <td>
                        Content, where any is available, issued on a showing of
                        probable cause under Rule 41 or an equivalent state
                        procedure. See Section 7 for important limits on
                        available content.
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </TableWrap>
              <P>
                We review each request for legal sufficiency and specificity. We
                may object to, narrow, or decline requests that are overbroad,
                vague, legally deficient, or that do not identify a specific
                account with reasonable particularity.
              </P>
            </Section>

            {/* 5. International */}
            <Section id="international">
              <SectionTitle>
                <span>05</span>Requests from Outside the United States
              </SectionTitle>
              <P>
                NudR is a U.S. entity and its records are held in the United
                States. Agencies outside the United States should generally
                proceed through a Mutual Legal Assistance Treaty (MLAT), letters
                rogatory, or another recognised channel of international legal
                cooperation.
              </P>
              <P>
                We may, at our discretion and where permitted by applicable law,
                respond voluntarily to a request from a non-U.S. authority —
                particularly where the request concerns an imminent safety
                threat under Section 3. Doing so in one instance does not oblige
                us to do so in any other.
              </P>
            </Section>

            {/* 6. Data available */}
            <Section id="data-available">
              <SectionTitle>
                <span>06</span>Information We May Hold
              </SectionTitle>
              <P>
                Subject to valid legal process and to the retention periods in
                our{" "}
                <Link to="/privacy" style={{ color: "#7c3aed" }}>
                  Privacy Policy
                </Link>
                , NudR may hold:
              </P>
              <List>
                <ListItem>
                  <strong>Basic subscriber information</strong> — the legal name
                  provided at registration, display name, email address, date of
                  birth, and account creation date.
                </ListItem>
                <ListItem>
                  <strong>Identity verification records</strong> — the
                  government-issued identification photograph submitted at
                  signup, which is stored encrypted and separately from the
                  public profile.
                </ListItem>
                <ListItem>
                  <strong>Device and connection records</strong> — IP addresses,
                  device identifiers bound to a session, and related security
                  and anti-fraud signals.
                </ListItem>
                <ListItem>
                  <strong>Profile content</strong> — photographs, bio, and
                  profile attributes as they exist at the time of the request.
                </ListItem>
                <ListItem>
                  <strong>Message metadata</strong> — sender, recipient,
                  timestamps, and delivery status. Metadata is not end-to-end
                  encrypted.
                </ListItem>
                <ListItem>
                  <strong>Account activity</strong> — matches, reports filed,
                  consent grants, enforcement actions, suspensions, and bans.
                </ListItem>
                <ListItem>
                  <strong>Billing records</strong> — subscription status and
                  transaction history. Full payment card numbers are held by our
                  payment processor, not by NudR.
                </ListItem>
                <ListItem>
                  <strong>Reported content</strong> — message or media content
                  associated with an active user report, which is retained for
                  investigation as described in our Terms.
                </ListItem>
              </List>
            </Section>

            {/* 7. Data NOT available */}
            <Section id="data-not-available">
              <SectionTitle>
                <span>07</span>Information We Cannot Provide
              </SectionTitle>
              <Callout>
                <P>
                  <strong>
                    Requesting agencies should read this section before
                    preparing a warrant.
                  </strong>{" "}
                  Because of how the Service is built, certain categories of
                  data do not exist in our systems and cannot be produced under
                  any legal process.
                </P>
              </Callout>
              <List>
                <ListItem>
                  <strong>Message content is end-to-end encrypted.</strong>{" "}
                  Messages between matched users are encrypted using keys
                  generated on the users' devices. NudR cannot read message
                  content in the ordinary course and cannot decrypt it on
                  request.
                </ListItem>
                <ListItem>
                  <strong>
                    Messages and shared media are deleted on a short schedule.
                  </strong>{" "}
                  Content that has been read or viewed is permanently deleted
                  within twenty-four (24) hours; content never read or viewed is
                  permanently deleted no later than thirty (30) days after
                  sending. Once deleted, it is unrecoverable. Requests should be
                  submitted promptly, and a preservation request (Section 8)
                  should be sent as early as possible.
                </ListItem>
                <ListItem>
                  <strong>We do not hold precise historical location.</strong>{" "}
                  Location is used to calculate distance while the app is in
                  foreground use and is displayed to other users only as an
                  approximate area. We do not maintain a continuous GPS location
                  history for accounts.
                </ListItem>
                <ListItem>
                  <strong>We do not store full payment card numbers.</strong>{" "}
                  Card data is processed and held by our payment processor;
                  requests for card details should be directed to that
                  processor.
                </ListItem>
                <ListItem>
                  <strong>Photo metadata is stripped on upload.</strong> EXIF,
                  GPS, and camera data are removed from images before storage.
                </ListItem>
              </List>
              <P>
                The single exception to end-to-end encryption is content
                associated with an active user report, which is decrypted for
                moderation review and retained for investigation as described in
                our{" "}
                <Link to="/terms" style={{ color: "#7c3aed" }}>
                  Terms of Service
                </Link>
                .
              </P>
            </Section>

            {/* 8. Preservation */}
            <Section id="preservation">
              <SectionTitle>
                <span>08</span>Preservation Requests
              </SectionTitle>
              <P>
                Given the short retention windows described above,{" "}
                <strong>
                  we strongly encourage agencies to submit a preservation
                  request at the earliest opportunity
                </strong>
                , in advance of, or alongside, formal legal process.
              </P>
              <P>
                On receipt of a valid preservation request under 18 U.S.C. §
                2703(f), we will take reasonable steps to preserve records then
                in our possession relating to the identified account for ninety
                (90) days, extendable on request. A preservation request does
                not itself compel disclosure, and it cannot restore data that
                was already deleted before the request was received.
              </P>
              <P>
                Preservation requests should be sent to{" "}
                <a href="mailto:legal@nudr.app">legal@nudr.app</a> and must
                identify the account with reasonable particularity.
              </P>
            </Section>

            {/* 9. Format */}
            <Section id="format">
              <SectionTitle>
                <span>09</span>What to Include in a Request
              </SectionTitle>
              <P>
                To allow us to locate the correct account and respond promptly,
                every request should include:
              </P>
              <List>
                <ListItem>
                  The requesting agency, the officer's name and rank, an
                  official government email address, and a direct telephone
                  number.
                </ListItem>
                <ListItem>
                  A specific account identifier — the email address associated
                  with the account, the username, or another identifier
                  sufficient to locate a single account.
                </ListItem>
                <ListItem>
                  The specific categories of records sought, described as
                  narrowly as possible.
                </ListItem>
                <ListItem>
                  The relevant date range for the records sought.
                </ListItem>
                <ListItem>
                  A response deadline that allows a reasonable period for review
                  and production.
                </ListItem>
                <ListItem>
                  Where applicable, the case or reference number and the
                  offence(s) under investigation.
                </ListItem>
              </List>
              <Callout>
                <P>
                  Requests that identify accounts only by first name,
                  photograph, approximate location, or partial information
                  generally cannot be processed. We are unable to conduct
                  open-ended searches of our user base.
                </P>
              </Callout>
            </Section>

            {/* 10. Response */}
            <Section id="response">
              <SectionTitle>
                <span>10</span>Our Response Process
              </SectionTitle>
              <P>
                We acknowledge receipt of law enforcement requests and review
                each one for legal sufficiency, scope, and specificity.
                Emergency requests under Section 3 are prioritised. Response
                times for non-emergency requests vary with the complexity and
                breadth of the request.
              </P>
              <P>
                Records are ordinarily produced electronically to the official
                government email address of the requesting officer. Where
                certified or authenticated records are required, please state
                this in the request.
              </P>
              <P>
                We may seek clarification, request that a demand be narrowed, or
                object where a request is legally deficient or overbroad.
              </P>
            </Section>

            {/* 11. Notice */}
            <Section id="notice">
              <SectionTitle>
                <span>11</span>Notice to Users
              </SectionTitle>
              <P>
                Our policy is to notify users when their information is sought
                by legal process, so that they may seek to protect their rights,
                unless we are prohibited from doing so by law or court order, or
                where we determine in good faith that notice would:
              </P>
              <List>
                <ListItem>
                  Create a risk of injury, death, or other serious harm to any
                  person;
                </ListItem>
                <ListItem>
                  Risk the destruction of evidence or otherwise frustrate an
                  ongoing investigation; or
                </ListItem>
                <ListItem>
                  Be counterproductive in a matter involving child exploitation
                  or an imminent safety threat.
                </ListItem>
              </List>
              <P>
                Where an agency requires that notice be delayed, the request
                should include the legal basis and the period of delay sought.
              </P>
            </Section>

            {/* 12. CSAM */}
            <Section id="csam">
              <SectionTitle>
                <span>12</span>Child Safety Reporting
              </SectionTitle>
              <CriticalCallout>
                <CriticalLabel>Zero tolerance</CriticalLabel>
                <P>
                  NudR is a strictly 18+ platform. Where we identify apparent
                  child sexual abuse material or the sexual exploitation of a
                  minor, we{" "}
                  <strong>
                    terminate the account, preserve the relevant evidence, and
                    report to the National Center for Missing &amp; Exploited
                    Children (NCMEC)
                  </strong>{" "}
                  as required by 18 U.S.C. § 2258A, and to law enforcement as
                  applicable. Agencies following up on a NCMEC CyberTipline
                  report should reference the CyberTipline report number when
                  contacting us.
                </P>
              </CriticalCallout>
            </Section>

            {/* 13. Costs */}
            <Section id="costs">
              <SectionTitle>
                <span>13</span>Costs and Authentication
              </SectionTitle>
              <P>
                NudR may seek reimbursement for the costs of responding to legal
                process where permitted by applicable law, particularly for
                requests that are unusually broad or burdensome.
              </P>
              <P>
                We may take steps to verify that a request is genuine, including
                confirming that it originates from an official government email
                domain and contacting the requesting agency directly. Requests
                sent from personal or non-official email accounts will not be
                processed.
              </P>
            </Section>

            {/* 14. No waiver */}
            <Section id="no-waiver">
              <SectionTitle>
                <span>14</span>No Waiver
              </SectionTitle>
              <P>
                These Guidelines are provided for informational purposes only.
                They do not create any rights or obligations, do not constitute
                legal advice, and are not a contract. Nothing in these
                Guidelines waives any right, objection, or defence available to
                NudR or to its users, including any objection to jurisdiction,
                venue, or the sufficiency of service.
              </P>
              <P>
                NudR may update these Guidelines at any time. The version
                published on this page is the current version.
              </P>

              <ContactList>
                <ContactRow>
                  <b>Law Enforcement Requests:</b>{" "}
                  <a href="mailto:legal@nudr.app">legal@nudr.app</a>
                </ContactRow>
                <ContactRow>
                  <b>Trust &amp; Safety:</b>{" "}
                  <a href="mailto:security@nudr.app">security@nudr.app</a>
                </ContactRow>
                <ContactRow>
                  <b>Registered Address:</b> NUDR INC, 1105 N Buena Vista
                  Street, Burbank, CA 91505, U.S.A.
                </ContactRow>
              </ContactList>

              <P style={{ marginTop: "1.4rem", fontSize: "0.85rem" }}>
                © {new Date().getFullYear()} NudR. All rights reserved.
              </P>

              <BackHome to="/">← Back to home</BackHome>
            </Section>
          </Content>
        </Layout>
      </Container>
    </Page>
  );
}
